/*!
 * Header Predictive Search - AJAX search for header inputs
 */
(function() {
  'use strict';

  const DEBOUNCE_DELAY = 250;
  let debounceTimer = null;
  let abortController = null;

  function debounce(fn, delay) {
    return function(...args) {
      clearTimeout(debounceTimer);
      debounceTimer = setTimeout(() => fn.apply(this, args), delay);
    };
  }

  function getQuery(input) {
    return input.value.trim();
  }

  function showLoading(input) {
    input.classList.add('is-loading');
    const parent = input.closest('.f-search, .mh-search-row');
    if (parent) {
      parent.classList.add('search-loading');
    }
  }

  function hideLoading(input) {
    input.classList.remove('is-loading');
    const parent = input.closest('.f-search, .mh-search-row');
    if (parent) {
      parent.classList.remove('search-loading');
    }
  }

  function getSearchResults(input, searchTerm) {
    // Get search URL - handle both form action and default
    let searchUrl = '/search';
    const form = input.closest('form');
    if (form && form.getAttribute('action')) {
      searchUrl = form.getAttribute('action');
    }

    console.log('[HeaderSearch] Searching for:', searchTerm, 'URL:', searchUrl);

    if (abortController) {
      abortController.abort();
    }

    abortController = new AbortController();
    showLoading(input);

    const fetchUrl = `${searchUrl}/suggest?q=${encodeURIComponent(searchTerm)}&section_id=predictive-search`;
    console.log('[HeaderSearch] Fetch URL:', fetchUrl);

    fetch(fetchUrl, {
      signal: abortController.signal
    })
      .then(response => {
        console.log('[HeaderSearch] Response status:', response.status);
        if (!response.ok) {
          throw new Error(response.status);
        }
        return response.text();
      })
      .then(text => {
        console.log('[HeaderSearch] Got response, length:', text.length);
        const parser = new DOMParser();
        const doc = parser.parseFromString(text, 'text/html');
        const section = doc.querySelector('#shopify-section-predictive-search');

        if (section) {
          console.log('[HeaderSearch] Found section, showing results');
          showResults(input, section.innerHTML);
        } else {
          console.log('[HeaderSearch] No section found in response');
          hideResults(input);
        }
      })
      .catch(error => {
        if (error.name === 'AbortError') return;
        console.error('[HeaderSearch] Search error:', error);
      })
      .finally(() => {
        hideLoading(input);
        abortController = null;
      });
  }

  function showResults(input, html) {
    const container = input.closest('.f-search, .mh-search-row');
    if (!container) return;

    let results = container.querySelector('.header-search-results');

    if (!results) {
      results = document.createElement('div');
      results.className = 'header-search-results';
      container.appendChild(results);
    }

    // Parse the HTML to extract only products
    const parser = new DOMParser();
    const doc = parser.parseFromString(html, 'text/html');

    // Find the products panel only
    const productsPanel = doc.querySelector('.predictive-search__panel--products');

    if (productsPanel) {
      // Remove any buttons like "search for"
      const buttons = productsPanel.querySelectorAll('.predictive-search__btn, .predictive-search__search-for');
      buttons.forEach(btn => btn.remove());

      // Get product list
      const productList = productsPanel.querySelector('.predictive-search__results-list');

      if (productList && productList.children.length > 0) {
        // Build clean product-only HTML
        results.innerHTML = `
          <div class="predictive-search">
            <div class="predictive-search__panels">
              <div class="predictive-search__panel" data-type="products">
                <ul class="predictive-search__results-list o-list-bare">
                  ${productList.innerHTML}
                </ul>
              </div>
            </div>
          </div>
        `;
        results.classList.add('is-visible');

        // Update ID prefixes to avoid conflicts
        results.querySelectorAll('[id^="predictive-search"]').forEach(el => {
          el.id = 'header-' + el.id;
        });
      } else {
        // No products found
        results.innerHTML = '';
        results.classList.remove('is-visible');
      }
    } else {
      results.innerHTML = '';
      results.classList.remove('is-visible');
    }
  }

  function hideResults(input) {
    const container = input.closest('.f-search, .mh-search-row');
    if (!container) return;

    const results = container.querySelector('.header-search-results');
    if (results) {
      results.classList.remove('is-visible');
    }
  }

  function onInputChange(input) {
    const searchTerm = getQuery(input);
    console.log('[HeaderSearch] Input changed, term:', searchTerm);

    if (!searchTerm.length) {
      hideResults(input);
      hideLoading(input);
      if (abortController) {
        abortController.abort();
        abortController = null;
      }
      return;
    }

    getSearchResults(input, searchTerm);
  }

  function onKeyUp(event, input) {
    if (event.key === 'Escape') {
      hideResults(input);
      input.blur();
    }
  }

  function onFocusOut(input) {
    // Delay to allow clicking on results
    setTimeout(() => {
      if (!input.matches(':focus')) {
        hideResults(input);
      }
    }, 200);
  }

  function init() {
    console.log('[HeaderSearch] Initializing...');
    
    // Desktop header search
    const desktopInput = document.querySelector('.f-search input[type="search"]');
    console.log('[HeaderSearch] Desktop input found:', !!desktopInput);
    if (desktopInput) {
      const debouncedChange = debounce(() => onInputChange(desktopInput), DEBOUNCE_DELAY);

      desktopInput.addEventListener('input', debouncedChange);
      desktopInput.addEventListener('keyup', (e) => onKeyUp(e, desktopInput));
      desktopInput.addEventListener('blur', () => onFocusOut(desktopInput));
      console.log('[HeaderSearch] Desktop events attached');
    }

    // Mobile header search
    const mobileInput = document.querySelector('.mh-search-form input[type="search"]');
    console.log('[HeaderSearch] Mobile input found:', !!mobileInput);
    if (mobileInput) {
      const debouncedChange = debounce(() => onInputChange(mobileInput), DEBOUNCE_DELAY);

      mobileInput.addEventListener('input', debouncedChange);
      mobileInput.addEventListener('keyup', (e) => onKeyUp(e, mobileInput));
      mobileInput.addEventListener('blur', () => onFocusOut(mobileInput));
      console.log('[HeaderSearch] Mobile events attached');
    }

    // Close results when clicking outside
    document.addEventListener('click', (e) => {
      if (!e.target.closest('.f-search, .mh-search-row')) {
        document.querySelectorAll('.header-search-results').forEach(results => {
          results.classList.remove('is-visible');
        });
      }
    });
    
    console.log('[HeaderSearch] Initialization complete');
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
